import { useState, useEffect, useCallback, useRef } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { SectionCard } from "@/components/ui/section-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/auth";
import { projectsApi } from "@/lib/api";
import { useTranslation } from "react-i18next";
import {
  FolderOpen,
  Plus,
  RefreshCw,
  Trash2,
  Search,
  Upload,
  Pencil,
  FileText,
  Users,
  Loader2,
  AlertTriangle,
  Settings,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Sender, ProjectFile } from "@/types/api";

import { ProjectDocuments } from "@/components/projects/ProjectDocuments";

interface ProjectData {
  id: number;
  name: string;
  created_at: string;
  prompt_id: string | null;
  files_count: number;
  senders_count: number;
}

const Projects = () => {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectData | null>(null);
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [projectSenders, setProjectSenders] = useState<Sender[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditPromptDialogOpen, setIsEditPromptDialogOpen] = useState(false);
  const [editPromptId, setEditPromptId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingMd, setIsUploadingMd] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
  const [deletingSenderId, setDeletingSenderId] = useState<number | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mdFileInputRef = useRef<HTMLInputElement>(null);
  
  const [isAddSenderDialogOpen, setIsAddSenderDialogOpen] = useState(false);
  const [newSenderName, setNewSenderName] = useState("");
  const [newSenderSurname, setNewSenderSurname] = useState("");
  const [newSenderGender, setNewSenderGender] = useState<"male" | "female" | "other">("male");
  const [newSenderVoiceId, setNewSenderVoiceId] = useState("");
  const [newSenderCalendlyLink, setNewSenderCalendlyLink] = useState("");

  const [isEditSenderDialogOpen, setIsEditSenderDialogOpen] = useState(false);
  const [editingSender, setEditingSender] = useState<Sender | null>(null);
  const [editSenderName, setEditSenderName] = useState("");
  const [editSenderSurname, setEditSenderSurname] = useState("");
  const [editSenderGender, setEditSenderGender] = useState<"male" | "female" | "other">("male");
  const [editSenderVoiceId, setEditSenderVoiceId] = useState("");
  const [editSenderCalendlyLink, setEditSenderCalendlyLink] = useState("");
  const [isEditingSender, setIsEditingSender] = useState(false);
  const [isBatchUpdating, setIsBatchUpdating] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setConnectionError(null);
    try {
      const response = await authFetch(
        projectsApi(`action=list_collections`)
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (errorData.externalApiError) {
          setConnectionError(errorData.error || t('connectionError'));
          setProjects([]);
          return;
        }
        
        throw new Error(errorData.error || `Failed to fetch projects: ${response.status}`);
      }

      const collectionsData = await response.json();
      const collections = collectionsData.collections || collectionsData || [];
      const projectsList: ProjectData[] = Array.isArray(collections) 
        ? collections.map((col: any, index: number) => ({
            id: typeof col === 'string' ? index + 1 : (col.id || index + 1),
            name: typeof col === 'string' ? col : (col.name || col.collection_name),
            created_at: typeof col === 'string' ? new Date().toISOString() : (col.created_at || new Date().toISOString()),
            prompt_id: typeof col === 'string' ? null : (col.prompt_id || null),
            files_count: typeof col === 'string' ? 0 : (col.files_count || col.document_count || 0),
            senders_count: typeof col === 'string' ? 0 : (col.senders_count || 0),
          }))
        : [];

      setProjects(projectsList);
      setConnectionError(null);
      
      if (projectsList.length > 0 && !selectedProject) {
        setSelectedProject(projectsList[0]);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setConnectionError(error instanceof Error ? error.message : t('errorFetchProjects'));
      toast({
        title: tc('error'),
        description: t('errorFetchProjects'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchProjectDetails = useCallback(async (project: ProjectData) => {
    setIsLoadingDetails(true);
    try {
      const projectResponse = await authFetch(
        projectsApi(`action=get_project&project_name=${encodeURIComponent(project.name)}`)
      );

      let projectId = project.id;
      let promptId = project.prompt_id;
      
      if (projectResponse.ok) {
        const projectData = await projectResponse.json();
        const projectInfo = projectData.project || projectData;
        projectId = projectInfo.id || project.id;
        promptId = projectInfo.prompt_id || project.prompt_id;
        
        setSelectedProject(prev => prev ? {
          ...prev,
          id: projectId,
          prompt_id: promptId,
        } : null);
      }

      const filesResponse = await authFetch(
        projectsApi(`action=browse_collection&collection_name=${encodeURIComponent(project.name)}`)
      );

      if (filesResponse.ok) {
        const filesData = await filesResponse.json();
        const filesList = filesData.data || filesData.documents || filesData || [];
        const files: ProjectFile[] = Array.isArray(filesList) 
          ? filesList.map((doc: any, index: number) => ({
              id: index + 1,
              name: typeof doc === 'string' ? doc : (doc.filename || doc.name || doc.document_name || `Document ${index + 1}`),
              size: typeof doc === 'string' ? 0 : (doc.size || 0),
              uploaded_at: typeof doc === 'string' ? new Date().toISOString() : (doc.uploaded_at || new Date().toISOString()),
            }))
          : [];
        setProjectFiles(files);
      } else {
        setProjectFiles([]);
      }

      const sendersResponse = await authFetch(
        projectsApi(`action=get_senders&project_id=${projectId}`)
      );

      if (sendersResponse.ok) {
        const sendersData = await sendersResponse.json();
        const sendersArray = sendersData.senders || sendersData || [];
        const senders: Sender[] = Array.isArray(sendersArray) 
          ? sendersArray.map((s: any) => ({
              id: s.id,
              name: s.name,
              surname: s.surname,
              gender: s.sex || s.gender || undefined,
              voice_id: s.voice_id,
              calendly_link: s.calendly_link,
              project_id: projectId,
              created_at: s.created_at || new Date().toISOString(),
            }))
          : [];
        setProjectSenders(senders);
      } else {
        setProjectSenders([]);
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetails(selectedProject);
    }
  }, [selectedProject?.name]);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast({
        title: tc('error'),
        description: t('errorEmptyName'),
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await authFetch(
        projectsApi(`action=create_collection`),
        {
          method: 'POST',
          body: JSON.stringify({
            collection_name: newProjectName,
            prompt_id: "115873",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create project');
      }

      setNewProjectName("");
      setIsCreateDialogOpen(false);
      toast({
        title: tc('success'),
        description: t('projectCreated', { name: newProjectName }),
      });
      
      await fetchProjects();
    } catch (error) {
      console.error("Error creating project:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorCreateProject'),
        variant: "destructive",
      });
    }
  };

  const handleDeleteProject = async (project: ProjectData) => {
    try {
      const response = await authFetch(
        projectsApi(`action=delete_collection`),
        {
          method: 'POST',
          body: JSON.stringify({ collection_name: project.name }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete project');
      }

      if (selectedProject?.id === project.id) {
        setSelectedProject(null);
      }
      
      toast({
        title: tc('success'),
        description: t('projectDeleted', { name: project.name }),
      });
      
      await fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorDeleteProject'),
        variant: "destructive",
      });
    }
  };

  const handleEditPromptId = async () => {
    if (!selectedProject || !editPromptId.trim()) return;
    
    try {
      const response = await authFetch(
        projectsApi(`action=set_prompt&project_id=${selectedProject.id}&prompt_id=${encodeURIComponent(editPromptId)}`),
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update prompt ID');
      }

      setSelectedProject({ ...selectedProject, prompt_id: editPromptId });
      setProjects(projects.map(p => 
        p.id === selectedProject.id ? { ...p, prompt_id: editPromptId } : p
      ));
      setIsEditPromptDialogOpen(false);
      toast({
        title: tc('success'),
        description: t('promptIdUpdated'),
      });
    } catch (error) {
      console.error("Error updating prompt ID:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorUpdatePrompt'),
        variant: "destructive",
      });
    }
  };

  const handleAddSender = async () => {
    if (!selectedProject || !newSenderName.trim() || !newSenderSurname.trim()) {
      toast({
        title: tc('error'),
        description: t('errorEmptyFields'),
        variant: "destructive",
      });
      return;
    }

    try {
      const projectResponse = await authFetch(
        projectsApi(`action=get_project&project_name=${encodeURIComponent(selectedProject.name)}`)
      );
      
      if (!projectResponse.ok) throw new Error('Failed to get project details');
      
      const projectData = await projectResponse.json();
      const realProjectId = projectData.project?.id || projectData.id;
      
      if (!realProjectId) throw new Error('Could not determine project ID');

      const response = await authFetch(
        projectsApi(`action=add_sender&project_id=${realProjectId}`),
        {
          method: 'POST',
          body: JSON.stringify({
            name: newSenderName,
            surname: newSenderSurname,
            sex: newSenderGender,
            voice_id: newSenderVoiceId || null,
            calendly_link: newSenderCalendlyLink || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add sender');
      }

      setNewSenderName("");
      setNewSenderSurname("");
      setNewSenderGender("male");
      setNewSenderVoiceId("");
      setNewSenderCalendlyLink("");
      setIsAddSenderDialogOpen(false);
      toast({
        title: tc('success'),
        description: t('senderAdded', { name: `${newSenderName} ${newSenderSurname}` }),
      });
      
      await fetchProjectDetails(selectedProject);
    } catch (error) {
      console.error("Error adding sender:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorAddSender'),
        variant: "destructive",
      });
    }
  };

  const handleDeleteSender = async (sender: Sender) => {
    if (!selectedProject || deletingSenderId) return;
    
    setDeletingSenderId(sender.id);
    try {
      const projectResponse = await authFetch(
        projectsApi(`action=get_project&project_name=${encodeURIComponent(selectedProject.name)}`)
      );
      
      if (!projectResponse.ok) throw new Error('Failed to get project details');
      
      const projectData = await projectResponse.json();
      const realProjectId = projectData.project?.id || projectData.id || selectedProject.id;

      const response = await authFetch(
        projectsApi(`action=delete_sender&project_id=${realProjectId}&sender_id=${sender.id}`),
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete sender');
      }

      toast({
        title: tc('success'),
        description: t('senderDeleted', { name: `${sender.name} ${sender.surname}` }),
      });
      
      await fetchProjectDetails(selectedProject);
    } catch (error) {
      console.error("Error deleting sender:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorDeleteSender'),
        variant: "destructive",
      });
    } finally {
      setDeletingSenderId(null);
    }
  };

  const openEditSenderDialog = (sender: Sender) => {
    setEditingSender(sender);
    setEditSenderName(sender.name);
    setEditSenderSurname(sender.surname);
    setEditSenderGender(sender.gender || "male");
    setEditSenderVoiceId(sender.voice_id || "");
    setEditSenderCalendlyLink(sender.calendly_link || "");
    setIsEditSenderDialogOpen(true);
  };

  const handleEditSender = async () => {
    if (!selectedProject || !editingSender || !editSenderName.trim() || !editSenderSurname.trim()) {
      toast({
        title: tc('error'),
        description: t('errorEmptyFields'),
        variant: "destructive",
      });
      return;
    }

    setIsEditingSender(true);
    try {
      const projectResponse = await authFetch(
        projectsApi(`action=get_project&project_name=${encodeURIComponent(selectedProject.name)}`)
      );
      
      if (!projectResponse.ok) throw new Error('Failed to get project details');
      
      const projectData = await projectResponse.json();
      const realProjectId = projectData.project?.id || projectData.id || selectedProject.id;

      const response = await authFetch(
        projectsApi(`action=update_sender&project_id=${realProjectId}&sender_id=${editingSender.id}`),
        {
          method: 'POST',
          body: JSON.stringify({
            name: editSenderName,
            surname: editSenderSurname,
            sex: editSenderGender,
            voice_id: editSenderVoiceId || null,
            calendly_link: editSenderCalendlyLink || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update sender');
      }

      setIsEditSenderDialogOpen(false);
      setEditingSender(null);
      toast({
        title: tc('success'),
        description: t('senderUpdated', { name: `${editSenderName} ${editSenderSurname}` }),
      });
      
      await fetchProjectDetails(selectedProject);
    } catch (error) {
      console.error("Error editing sender:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorEditSender'),
        variant: "destructive",
      });
    } finally {
      setIsEditingSender(false);
    }
  };

  const handleDeleteFile = async (file: ProjectFile) => {
    if (!selectedProject || deletingFileId) return;
    
    setDeletingFileId(file.id);
    try {
      const response = await authFetch(
        projectsApi(`action=delete_document`),
        {
          method: 'POST',
          body: JSON.stringify({
            collection_name: selectedProject.name,
            document_name: file.name,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete file');
      }

      toast({
        title: tc('success'),
        description: t('fileDeleted', { name: file.name }),
      });
      
      await fetchProjectDetails(selectedProject);
    } catch (error) {
      console.error("Error deleting file:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorDeleteFile'),
        variant: "destructive",
      });
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedProject) return;

    if (file.type !== 'application/pdf') {
      toast({
        title: tc('error'),
        description: "Only PDF files are allowed",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('collection_name', selectedProject.name);
      formData.append('project_id', selectedProject.name);
      formData.append('file', file);

      const token = localStorage.getItem("auth_token");
      
      const response = await fetch(
        projectsApi(`action=upload_pdf`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'x-app-token': token || '',
            'x-api-base-url': 'beta',
          },
          body: formData,
        }
      );

      if (response.status === 401) {
        localStorage.removeItem("auth_token");
        window.location.reload();
        throw new Error("Session expired. Please log in again.");
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }

      toast({
        title: tc('success'),
        description: t('fileUploaded', { name: file.name }),
      });

      await fetchProjectDetails(selectedProject);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorUploadFile'),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleMdFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedProject) return;

    if (!file.name.toLowerCase().endsWith('.md')) {
      toast({
        title: tc('error'),
        description: t('errorOnlyMd'),
        variant: "destructive",
      });
      return;
    }

    setIsUploadingMd(true);
    try {
      const formData = new FormData();
      formData.append('collection_name', selectedProject.name);
      formData.append('file', file);

      const token = localStorage.getItem("auth_token");

      const response = await fetch(
        projectsApi(`action=upload_md`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'x-app-token': token || '',
            'x-api-base-url': 'beta',
          },
          body: formData,
        }
      );

      if (response.status === 401) {
        localStorage.removeItem("auth_token");
        window.location.reload();
        throw new Error("Session expired. Please log in again.");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload MD file');
      }

      toast({
        title: tc('success'),
        description: t('fileUploaded', { name: file.name }),
      });

      await fetchProjectDetails(selectedProject);
    } catch (error) {
      console.error("Error uploading MD file:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorUploadMdFile'),
        variant: "destructive",
      });
    } finally {
      setIsUploadingMd(false);
      if (mdFileInputRef.current) {
        mdFileInputRef.current.value = '';
      }
    }
  };

  const handleBatchUpdatePromptId = async () => {
    const newPromptId = prompt("Enter new Prompt ID for all projects (except Generic):");
    if (!newPromptId) return;
    
    setIsBatchUpdating(true);
    try {
      const response = await authFetch(
        projectsApi(`action=batch_set_prompt`),
        {
          method: 'POST',
          body: JSON.stringify({ prompt_id: newPromptId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to batch update');
      }

      const result = await response.json();
      toast({
        title: t('batchUpdate'),
        description: t('batchUpdateSuccess', { count: result.success }),
      });

      await fetchProjects();
      if (selectedProject) {
        await fetchProjectDetails(selectedProject);
      }
    } catch (error) {
      console.error("Error batch updating:", error);
      toast({
        title: tc('error'),
        description: error instanceof Error ? error.message : t('errorUpdatePrompt'),
        variant: "destructive",
      });
    } finally {
      setIsBatchUpdating(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <MainLayout>
      <SectionCard
        icon={FolderOpen}
        title={t('title')}
        description={t('title')}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchProjects} disabled={isLoading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              {tc('refresh')}
            </Button>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('createProject')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('createProjectTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('createProjectDescription')}
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="project-name">{t('projectName')}</Label>
                  <Input
                    id="project-name"
                    placeholder={t('projectNamePlaceholder')}
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    className="mt-2"
                    onKeyDown={(e) => e.key === "Enter" && handleCreateProject()}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    {tc('cancel')}
                  </Button>
                  <Button onClick={handleCreateProject}>
                    <Plus className="mr-2 h-4 w-4" />
                    {tc('create')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      >
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Projects List */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t('title')} ({projects.length})</span>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : connectionError ? (
              <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-6 text-center">
                <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
                <h3 className="font-semibold text-lg mb-2">{t('connectionErrorTitle')}</h3>
                <p className="text-sm text-muted-foreground mb-4">{connectionError}</p>
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button variant="outline" size="sm" onClick={fetchProjects}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {tc('refresh')}
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a href="/api-testing">
                      <Settings className="mr-2 h-4 w-4" />
                      {t('connectionErrorDescription')}
                    </a>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredProjects.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{searchTerm ? t('noResults') : t('noProjects')}</p>
                ) : (
                  filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      className={`group flex cursor-pointer items-center justify-between rounded-lg border p-4 transition-all hover:border-primary/50 ${
                        selectedProject?.id === project.id
                          ? "border-primary bg-primary/5"
                          : "border-border bg-card"
                      }`}
                      onClick={() => setSelectedProject(project)}
                    >
                      <div className="flex-1">
                        <p className="font-medium">{project.name}</p>
                        <p className="text-xs text-muted-foreground">ID: {project.id}</p>
                      </div>
                      {project.name !== 'Generic' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 transition-opacity group-hover:opacity-100 bg-destructive/10 hover:bg-destructive/20"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(project);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Project Details */}
          {selectedProject && (
            <div className="space-y-6 animate-fade-in">
              {/* Project Info Card */}
              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <FolderOpen className="h-5 w-5 text-primary" />
                  {selectedProject.name}
                </div>

                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-sm font-medium">
                      {new Date(selectedProject.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('files')}</p>
                    <p className="text-sm font-medium">{projectFiles.length}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('senders')}</p>
                    <p className="text-sm font-medium">{projectSenders.length}</p>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('promptId')}</p>
                    <p className="text-sm font-medium">{selectedProject.prompt_id || 'N/A'}</p>
                  </div>
                  <Dialog open={isEditPromptDialogOpen} onOpenChange={setIsEditPromptDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditPromptId(selectedProject.prompt_id || "")}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        {t('editPromptId')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t('editPromptIdTitle')}</DialogTitle>
                        <DialogDescription>
                          {t('editPromptIdDescription')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-4">
                        <Label htmlFor="prompt-id">{t('promptId')}</Label>
                        <Input
                          id="prompt-id"
                          placeholder={t('promptIdPlaceholder')}
                          value={editPromptId}
                          onChange={(e) => setEditPromptId(e.target.value)}
                          className="mt-2"
                          onKeyDown={(e) => e.key === "Enter" && handleEditPromptId()}
                        />
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditPromptDialogOpen(false)}>
                          {tc('cancel')}
                        </Button>
                        <Button onClick={handleEditPromptId}>
                          {tc('save')}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Upload PDF */}
              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center gap-2 font-semibold">
                  <Upload className="h-5 w-5 text-primary" />
                  {t('uploadFile')}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                
                <div 
                  className={`mt-4 flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                    isUploading 
                      ? 'border-muted bg-muted/20 cursor-not-allowed' 
                      : 'border-primary/50 bg-primary/5 hover:border-primary hover:bg-primary/10'
                  }`}
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                >
                  <div className="text-center">
                    {isUploading ? (
                      <>
                        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                        <p className="mt-2 text-sm text-muted-foreground">{tc('loading')}</p>
                      </>
                    ) : (
                      <>
                        <Upload className="mx-auto h-8 w-8 text-primary" />
                        <p className="mt-2 text-sm text-primary">{t('uploadFile')}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Upload Markdown - Generic project only */}
              {selectedProject.name === 'Generic' && (
                <div className="rounded-lg border border-border bg-card p-6">
                  <div className="flex items-center gap-2 font-semibold">
                    <Upload className="h-5 w-5 text-primary" />
                    {t('uploadMdFile')}
                  </div>

                  <input
                    ref={mdFileInputRef}
                    type="file"
                    accept=".md,text/markdown"
                    onChange={handleMdFileUpload}
                    className="hidden"
                  />

                  <div
                    className={`mt-4 flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                      isUploadingMd
                        ? 'border-muted bg-muted/20 cursor-not-allowed'
                        : 'border-primary/50 bg-primary/5 hover:border-primary hover:bg-primary/10'
                    }`}
                    onClick={() => !isUploadingMd && mdFileInputRef.current?.click()}
                  >
                    <div className="text-center">
                      {isUploadingMd ? (
                        <>
                          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                          <p className="mt-2 text-sm text-muted-foreground">{tc('loading')}</p>
                        </>
                      ) : (
                        <>
                          <Upload className="mx-auto h-8 w-8 text-primary" />
                          <p className="mt-2 text-sm text-primary">{t('uploadMdFile')}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Project Files */}
              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center gap-2 font-semibold">
                  <FileText className="h-5 w-5 text-primary" />
                  {t('files')} ({projectFiles.length})
                </div>

                <div className="mt-4 space-y-2">
                  {isLoadingDetails ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : projectFiles.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('noFiles')}</p>
                  ) : (
                    projectFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between rounded-lg border border-border bg-background p-3"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-destructive" />
                          <div>
                            <p className="text-sm font-medium">{file.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(file.size)} • {new Date(file.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="bg-destructive/10 hover:bg-destructive/20"
                          onClick={() => handleDeleteFile(file)}
                          disabled={deletingFileId === file.id}
                        >
                          {deletingFileId === file.id ? (
                            <Loader2 className="h-4 w-4 animate-spin text-destructive" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-destructive" />
                          )}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {selectedProject && (
                <ProjectDocuments projectId={selectedProject.id} projectName={selectedProject.name} />
              )}



              {/* Project Senders */}
              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-semibold">
                    <Users className="h-5 w-5 text-primary" />
                    {t('senders')} ({projectSenders.length})
                  </div>
                  <Dialog open={isAddSenderDialogOpen} onOpenChange={setIsAddSenderDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Users className="mr-2 h-4 w-4" />
                        {t('addSender')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          {t('addSender')}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="grid grid-cols-2 gap-4 py-4">
                        <div>
                          <Label htmlFor="sender-name">{t('senderName')}</Label>
                          <Input
                            id="sender-name"
                            placeholder={t('senderName')}
                            value={newSenderName}
                            onChange={(e) => setNewSenderName(e.target.value)}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="sender-surname">{t('senderSurname')}</Label>
                          <Input
                            id="sender-surname"
                            placeholder={t('senderSurname')}
                            value={newSenderSurname}
                            onChange={(e) => setNewSenderSurname(e.target.value)}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="sender-gender">{t('senderGender')}</Label>
                          <Select
                            value={newSenderGender}
                            onValueChange={(v: "male" | "female" | "other") => setNewSenderGender(v)}
                          >
                            <SelectTrigger className="mt-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">{t('male')}</SelectItem>
                              <SelectItem value="female">{t('female')}</SelectItem>
                              <SelectItem value="other">{t('other')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="sender-voice-id">{t('senderVoiceId')}</Label>
                          <Input
                            id="sender-voice-id"
                            placeholder="Voice ID"
                            value={newSenderVoiceId}
                            onChange={(e) => setNewSenderVoiceId(e.target.value)}
                            className="mt-2"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="sender-calendly">{t('senderCalendlyLink')}</Label>
                          <Input
                            id="sender-calendly"
                            placeholder="https://calendly.com/your-link"
                            value={newSenderCalendlyLink}
                            onChange={(e) => setNewSenderCalendlyLink(e.target.value)}
                            className="mt-2"
                            type="url"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button className="w-full" onClick={handleAddSender}>
                          <Users className="mr-2 h-4 w-4" />
                          {t('addSender')}
                        </Button>
                        <Button variant="outline" onClick={() => setIsAddSenderDialogOpen(false)}>
                          {tc('cancel')}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {/* Edit Sender Dialog */}
                  <Dialog open={isEditSenderDialogOpen} onOpenChange={setIsEditSenderDialogOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Pencil className="h-5 w-5" />
                          {t('editSender')}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="grid grid-cols-2 gap-4 py-4">
                        <div>
                          <Label htmlFor="edit-sender-name">{t('senderName')}</Label>
                          <Input
                            id="edit-sender-name"
                            placeholder={t('senderName')}
                            value={editSenderName}
                            onChange={(e) => setEditSenderName(e.target.value)}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-sender-surname">{t('senderSurname')}</Label>
                          <Input
                            id="edit-sender-surname"
                            placeholder={t('senderSurname')}
                            value={editSenderSurname}
                            onChange={(e) => setEditSenderSurname(e.target.value)}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-sender-gender">{t('senderGender')}</Label>
                          <Select
                            value={editSenderGender}
                            onValueChange={(v: "male" | "female" | "other") => setEditSenderGender(v)}
                          >
                            <SelectTrigger className="mt-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">{t('male')}</SelectItem>
                              <SelectItem value="female">{t('female')}</SelectItem>
                              <SelectItem value="other">{t('other')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="edit-sender-voice-id">{t('senderVoiceId')}</Label>
                          <Input
                            id="edit-sender-voice-id"
                            placeholder="Voice ID"
                            value={editSenderVoiceId}
                            onChange={(e) => setEditSenderVoiceId(e.target.value)}
                            className="mt-2"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="edit-sender-calendly">{t('senderCalendlyLink')}</Label>
                          <Input
                            id="edit-sender-calendly"
                            placeholder="https://calendly.com/your-link"
                            value={editSenderCalendlyLink}
                            onChange={(e) => setEditSenderCalendlyLink(e.target.value)}
                            className="mt-2"
                            type="url"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          className="w-full"
                          onClick={handleEditSender}
                          disabled={isEditingSender}
                        >
                          {isEditingSender ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {tc('loading')}
                            </>
                          ) : (
                            <>
                              <Pencil className="mr-2 h-4 w-4" />
                              {tc('save')}
                            </>
                          )}
                        </Button>
                        <Button variant="outline" onClick={() => setIsEditSenderDialogOpen(false)} disabled={isEditingSender}>
                          {tc('cancel')}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="mt-4 space-y-2">
                  {isLoadingDetails ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : projectSenders.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('noSenders')}</p>
                  ) : (
                    projectSenders.map((sender) => (
                      <div
                        key={sender.id}
                        className="flex items-center justify-between rounded-lg border border-border bg-background p-3"
                      >
                        <div className="flex items-center gap-3">
                          <Users className="h-5 w-5 text-primary" />
                          <div>
                            <p className="text-sm font-medium">{sender.name} {sender.surname}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('senderGender')}: {sender.gender === 'male' ? 'M' : sender.gender === 'female' ? 'K' : sender.gender ? 'I' : '-'} • Voice ID: {sender.voice_id || '-'}
                              {sender.calendly_link && <> • Calendly: ✓</>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="bg-primary/10 hover:bg-primary/20"
                            onClick={() => openEditSenderDialog(sender)}
                          >
                            <Pencil className="h-4 w-4 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="bg-destructive/10 hover:bg-destructive/20"
                            onClick={() => handleDeleteSender(sender)}
                            disabled={deletingSenderId === sender.id}
                          >
                            {deletingSenderId === sender.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-destructive" />
                            ) : (
                              <Trash2 className="h-4 w-4 text-destructive" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </SectionCard>
    </MainLayout>
  );
};

export default Projects;
